import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PageHeader, EmptyState } from '@/components/Shared';
import { money, num, shortDate } from '@/lib/format';
import { ShoppingCart, Plus } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { supplier_name: '', purchase_date: '', product_name: '', brand: '', sku: '', quantity: 1, total_cost: 0, notes: '' };

export default function Purchases() {
  const [purchases, setPurchases] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => { const { data } = await api.get('/purchases'); setPurchases(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post('/purchases', { ...form, quantity: Number(form.quantity), total_cost: Number(form.total_cost), purchase_date: form.purchase_date || undefined });
      toast.success('Purchase recorded, inventory updated'); setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const unitCost = form.quantity > 0 ? Number(form.total_cost) / Number(form.quantity) : 0;

  return (
    <div>
      <PageHeader title="Purchases" subtitle="Incoming inventory from suppliers" testid="purchases-header"
        actions={<Button onClick={() => setOpen(true)} data-testid="purchases-add-button"><Plus className="h-4 w-4 mr-2" /> Record Purchase</Button>} />

      <Card className="p-0 overflow-hidden">
        {purchases.length === 0 ? <div className="p-6"><EmptyState icon={ShoppingCart} title="No purchases yet" description="Record a purchase to increase inventory and track cost." /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Supplier</TableHead><TableHead>Product</TableHead><TableHead>SKU</TableHead>
                <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Unit Cost</TableHead><TableHead className="text-right">Total</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {purchases.map((p) => (
                  <TableRow key={p.id} data-testid="purchase-row">
                    <TableCell>{shortDate(p.created_at)}</TableCell>
                    <TableCell>{p.supplier_name}</TableCell>
                    <TableCell>{p.product_name}</TableCell>
                    <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(p.quantity)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(p.cost_per_unit)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{money(p.total_cost)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Record Purchase</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Supplier Name</Label><Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} data-testid="purchase-supplier-input" className="mt-1.5" /></div>
            <div><Label>Purchase Date</Label><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Product Name</Label><Input value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Brand</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1.5" /></div>
            <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="purchase-sku-input" className="mt-1.5" /><p className="text-xs text-muted-foreground mt-1">Existing SKU updates stock; new SKU creates product.</p></div>
            <div><Label>Quantity Purchased</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="purchase-qty-input" className="mt-1.5" /></div>
            <div><Label>Total Purchase Cost</Label><Input type="number" step="0.01" value={form.total_cost} onChange={(e) => setForm({ ...form, total_cost: e.target.value })} data-testid="purchase-cost-input" className="mt-1.5" /></div>
            <div><Label>Cost Per Unit (auto)</Label><Input value={money(unitCost)} disabled className="mt-1.5" /></div>
            <div className="md:col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="purchase-form-submit-button">Save Purchase</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
